import { useMemo, useState } from "react";
import { LayoutGrid, List, Plus, Settings, Users } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { CreateProjectDialog } from "@/components/projects/create-project-dialog";
import { OnboardingCard } from "@/components/projects/onboarding-card";
import { ProjectCard } from "@/components/projects/project-card";
import { useWindowedList } from "@/hooks/use-windowed-list";
import { ProjectSortMenu, type SortState } from "@/components/projects/project-sort-menu";
import { ManageMembersDialog } from "@/components/members/manage-members-dialog";
import { useAuth } from "@/hooks/use-auth";
import { useProjects } from "@/hooks/use-projects";
import { canCreateProject, canManageMembers } from "@/lib/api-types";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";

type Props = {
  /** "/app/projects" or "/admin/projects" — used to build per-project links and the create-redirect. */
  basePath: string;
  /**
   * Historical: used to route the people icon to a members page. Kept as an
   * OPT-IN visibility flag for the icon; clicking it now opens the
   * ManageMembersDialog modal instead of navigating. Omit to hide the icon.
   */
  editorsPath?: string;
  /** Where the "..." menu's settings item should go. Omit to hide the menu. */
  settingsPath?: string;
};

function initialsFrom(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function ProjectsPage({ basePath, editorsPath, settingsPath }: Props) {
  const { role, workspaceName } = useAuth();
  const { data: projects, isLoading, isError, refetch } = useProjects();
  const [view, setView] = useState<"grid" | "list">("grid");
  const [sort, setSort] = useState<SortState>({ field: "name", dirIndex: 0 });
  const [createOpen, setCreateOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);

  const displayName = workspaceName || "Workspace";
  const showInvite = !!editorsPath && canManageMembers(role);
  const showCreate = canCreateProject(role);
  const showMenu = !!settingsPath;

  const sorted = useMemo(() => {
    if (!projects) return [];
    const copy = [...projects];

    // Primary field sort. Each branch documents its dirIndex=0 orientation
    // (matches the labels in project-sort-menu.tsx DIRECTION_LABELS).
    if (sort.field === "name") {
      // 0 = A → Z, 1 = Z → A
      copy.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sort.field === "dateCreated") {
      // 0 = Newest → Oldest
      copy.sort(
        (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
      );
    } else if (sort.field === "dateUpdated") {
      // 0 = Newest → Oldest. Falls back to createdAt when the API predates
      // the enrichment (defensive — the enriched endpoint always sends it now).
      copy.sort((a, b) => {
        const at = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime();
        const bt = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime();
        return at - bt;
      });
    } else if (sort.field === "storage") {
      // 0 = Largest → Smallest. Null/undefined storageBytes treated as 0.
      copy.sort((a, b) => (b.storageBytes ?? 0) - (a.storageBytes ?? 0));
    } else if (sort.field === "status") {
      // 0 = Active → Inactive. The trailing inactive-to-bottom pass below
      // enforces the same rule regardless of primary sort, so this branch
      // effectively falls back to default (createdAt) order within each
      // status group — which is exactly what the label promises.
      copy.sort(
        (a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
      );
    }

    if (sort.dirIndex === 1) copy.reverse();

    // Regardless of the primary sort, INACTIVE projects sink to the bottom of
    // the visible list. Frame.io does the same thing — archived projects
    // don't hijack the top of the grid but stay accessible. Stable sort
    // guaranteed by Array.prototype.sort in modern engines, so the primary
    // ordering within each group is preserved.
    //
    // Status sort with dirIndex=1 (Inactive → Active) means "show inactive
    // first" — override the sink in that case so the toggle actually flips.
    const putInactiveFirst = sort.field === "status" && sort.dirIndex === 1;
    copy.sort((a, b) => {
      const aInactive = a.inactiveAt ? 1 : 0;
      const bInactive = b.inactiveAt ? 1 : 0;
      return putInactiveFirst ? bInactive - aInactive : aInactive - bInactive;
    });
    // Sample "Start here" project always pins to the top (onboarding, 2026-10-31).
    copy.sort((a, b) => (b.isSample ? 1 : 0) - (a.isSample ? 1 : 0));
    return copy;
  }, [projects, sort]);

  // Only mount a window of the (already sorted) list; append on scroll.
  const { visible: pagedProjects, hasMore, sentinelRef } = useWindowedList(sorted);

  return (
    <div className="flex h-[calc(100dvh-3.5rem)] flex-col px-4 md:h-[calc(100dvh-4rem)] md:px-8">
      {/* Fixed header block — never scrolls; only the card grid below scrolls */}
      <div className="shrink-0 pt-4">
      {/* Header: workspace identity + primary actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="text-xs">{initialsFrom(displayName)}</AvatarFallback>
          </Avatar>
          <h1 className="font-display text-2xl font-bold tracking-tight">{displayName}</h1>
        </div>

        <div className="flex items-center gap-2">
          {showInvite && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setMembersOpen(true)}
                  aria-label="Manage workspace members"
                  className="grid h-9 w-9 place-items-center rounded-full bg-secondary text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Users className="h-4 w-4" strokeWidth={1.75} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Manage members</TooltipContent>
            </Tooltip>
          )}

          {showCreate && (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              New Project
            </Button>
          )}

          {showMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="More options">
                  <span className="text-lg leading-none">···</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to={settingsPath!}>
                    <Settings />
                    Workspace settings
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Toolbar: view toggle + sort. "Filtered by All Projects" pill removed —
          no real project filters exist yet and it wasn't a control. */}
      <div className="mt-3 flex items-center gap-4 border-b border-border pb-3 text-sm">
        <div className="flex items-center gap-0.5 rounded-lg border border-border p-0.5">
          <button
            type="button"
            aria-label="Grid view"
            aria-pressed={view === "grid"}
            onClick={() => setView("grid")}
            className={cn(
              "grid h-7 w-7 place-items-center rounded-md transition-colors",
              view === "grid"
                ? "bg-secondary text-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <LayoutGrid className="h-4 w-4" strokeWidth={1.75} />
          </button>
          <button
            type="button"
            aria-label="List view"
            aria-pressed={view === "list"}
            onClick={() => setView("list")}
            className={cn(
              "grid h-7 w-7 place-items-center rounded-md transition-colors",
              view === "list"
                ? "bg-secondary text-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <List className="h-4 w-4" strokeWidth={1.75} />
          </button>
        </div>

        <ProjectSortMenu value={sort} onChange={setSort} />
      </div>
      </div>

      {/* Content — the only scrollable region on the page */}
      <div className="scrollbar-none min-h-0 flex-1 overflow-y-auto pt-6">
        {showCreate && (
          <OnboardingCard
            basePath={basePath}
            projects={projects}
            onCreateProject={() => setCreateOpen(true)}
          />
        )}
        {isLoading && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="aspect-video animate-pulse rounded-xl bg-secondary/60" />
            ))}
          </div>
        )}

        {isError && !isLoading && (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-muted-foreground">Couldn't load projects.</p>
            <Button variant="outline" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        )}

        {!isLoading && !isError && sorted.length === 0 && (
          <div className="flex flex-col items-center gap-3 py-24 text-center">
            <div className="grid grid-cols-2 gap-1.5">
              {Array.from({ length: 4 }).map((_, i) => (
                <span key={i} className="h-6 w-6 rounded-md bg-secondary" />
              ))}
            </div>
            <h2 className="mt-2 text-lg font-semibold text-foreground">
              Looks like you don't have any Projects in {displayName}
            </h2>
            <p className="max-w-sm text-sm text-muted-foreground">
              Projects are where you create and organize your videos.
            </p>
            {showCreate && (
              <Button className="mt-2" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" />
                New Project
              </Button>
            )}
          </div>
        )}

        {!isLoading && !isError && sorted.length > 0 && (
          <div
            className={cn(
              view === "grid"
                ? "grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
                : "flex flex-col gap-2"
            )}
          >
            {pagedProjects.map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                to={`${basePath}/${project.id}`}
                view={view}
              />
            ))}
          </div>
        )}
        {hasMore && <div ref={sentinelRef} aria-hidden className="h-1" />}
      </div>

      {showCreate && (
        <CreateProjectDialog open={createOpen} onOpenChange={setCreateOpen} basePath={basePath} />
      )}
      {showInvite && (
        <ManageMembersDialog open={membersOpen} onOpenChange={setMembersOpen} />
      )}
    </div>
  );
}
